/**
 * Световые дуги на фоне входа.
 *
 * SVG, а не CSS-кольца. Кольцо с градиентной обводкой даёт ровную по
 * толщине окружность и мягкое пятно вместо линии; здесь нужна дуга,
 * которая гаснет к концам и светится, — а это форма, толщина и размытие
 * порознь, то есть путь со своим градиентом и своим фильтром.
 *
 * Каждая дуга рисуется дважды: широкая размытая — свет, поверх неё
 * тонкая яркая — сама линия. Одним путём такого не получить: размытие
 * съело бы линию, а без размытия не было бы свечения.
 *
 * Координаты — в системе макета 1536×1024, и картинка обрезается по
 * краям (`slice`), а не растягивается: растянутая дуга перестаёт быть
 * дугой на широком мониторе.
 */

import type { JSX } from 'react';

export function AuthArcs(): JSX.Element {
  return (
    <svg
      className="auth-arcs"
      viewBox="0 0 1536 1024"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        {/*
          Прозрачность на концах — часть градиента, а не отдельная маска.
          Так дуга уходит в темноту сама, и её не приходится обрезать
          краем экрана.
        */}
        <linearGradient id="arc-top" x1="0.75" y1="0" x2="0" y2="0.9">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
          <stop offset="18%" stopColor="#38bdf8" stopOpacity="0.95" />
          <stop offset="42%" stopColor="#818cf8" stopOpacity="1" />
          <stop offset="68%" stopColor="#a855f7" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>

        <linearGradient id="arc-bottom" x1="0" y1="1" x2="1" y2="0.2">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0" />
          <stop offset="24%" stopColor="#4f46e5" stopOpacity="0.85" />
          <stop offset="52%" stopColor="#22d3ee" stopOpacity="1" />
          <stop offset="78%" stopColor="#d946ef" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#d946ef" stopOpacity="0" />
        </linearGradient>

        <filter id="arc-glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="26" />
        </filter>
        <filter id="arc-halo" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="70" />
        </filter>
      </defs>

      {/* Верхняя дуга: входит сверху и уходит за левый край. */}
      <g className="auth-arc-top">
        <path d={TOP} stroke="url(#arc-top)" strokeWidth="26" filter="url(#arc-halo)" />
        <path d={TOP} stroke="url(#arc-top)" strokeWidth="9" filter="url(#arc-glow)" />
        <path d={TOP} stroke="url(#arc-top)" strokeWidth="2.4" />
      </g>

      {/* Нижняя: идёт понизу и уходит за правый край. */}
      <g className="auth-arc-bottom">
        <path d={BOTTOM} stroke="url(#arc-bottom)" strokeWidth="26" filter="url(#arc-halo)" />
        <path d={BOTTOM} stroke="url(#arc-bottom)" strokeWidth="9" filter="url(#arc-glow)" />
        <path d={BOTTOM} stroke="url(#arc-bottom)" strokeWidth="2.4" />
      </g>
    </svg>
  );
}

/*
 * Кривые Безье, а не дуги окружности.
 *
 * На макете обе линии к концам распрямляются — у окружности так не
 * бывает, у неё кривизна одна на всю длину. Разница заметна именно на
 * концах, там, где линия уходит за край.
 */
const TOP = 'M 640 -60 C 372 74, 150 268, -60 596';
const BOTTOM = 'M 250 1084 C 700 1006, 1180 906, 1600 470';
